import { useComputedColorScheme } from "@mantine/core";
import { useMemo } from "react";

import {
  base,
  blue,
  error,
  gray,
  grayBlue,
  grayDarkMode,
  orange,
  pink,
  success,
  warning,
} from "../Colors";

/**
 * Mode-resolved hex values for code that cannot consume CSS variables —
 * primarily Highcharts option objects and CSV/PNG exports.
 *
 * Values reproduce the legacy MUI palette (`getDefaultTheme` in
 * `src/utils/Theme/index.ts`) exactly, including the mode-inverted
 * `utility.*` scales, so charts keep their colors as `useTheme().palette.*`
 * call sites migrate. For styling, prefer the `--mantine-color-*` CSS
 * variables from `cssVariablesResolver`.
 */

const UTILITY_STEPS = [50, 100, 200, 300, 400, 500, 600, 700] as const;
type UtilityStep = (typeof UTILITY_STEPS)[number];
type UtilityScale = Record<UtilityStep, string>;
type ColorScale = Record<number, string>;

// In dark mode the utility scales invert around 500 (50↔950, 100↔900, …)
const buildUtilityScale = (
  scale: ColorScale,
  isLightMode: boolean,
): UtilityScale =>
  Object.fromEntries(
    UTILITY_STEPS.map((step) => [
      step,
      isLightMode ? scale[step] : scale[1000 - step],
    ]),
  ) as UtilityScale;

export const getSemanticColors = (isLightMode: boolean) => ({
  mode: isLightMode ? ("light" as const) : ("dark" as const),
  background: {
    default: isLightMode ? "#FFFFFF" : grayDarkMode[950],
    paper: isLightMode ? "#FFFFFF" : grayDarkMode[950],
  },
  primary: {
    main: isLightMode ? grayBlue[600] : grayBlue[500],
    light: isLightMode ? grayBlue[400] : grayBlue[700],
    dark: isLightMode ? grayBlue[800] : grayBlue[300],
    contrastText: isLightMode ? "#FFFFFF" : grayBlue[950],
  },
  // MUI default status mains (the legacy theme never overrode these)
  error: { main: "#d32f2f" },
  warning: { main: "#ed6c02" },
  success: { main: "#2e7d32" },
  info: { main: "#0288d1" },
  // MUI default grey scale (theme.palette.grey was never themed)
  grey: {
    100: "#f5f5f5",
    200: "#eeeeee",
    300: "#e0e0e0",
    400: "#bdbdbd",
    500: "#9e9e9e",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
  action: {
    active: isLightMode ? "rgba(0, 0, 0, 0.54)" : "#FFFFFF",
  },
  mybuttonborder: {
    primary: grayBlue[600],
    secondary: isLightMode ? gray[300] : gray[700],
  },
  text: {
    primary: isLightMode ? gray[900] : grayDarkMode[50],
    secondary: isLightMode ? gray[700] : grayDarkMode[300],
    disabled: isLightMode ? gray[500] : grayDarkMode[500],
    icon: isLightMode ? gray[500] : grayDarkMode[500],
  },
  divider: isLightMode ? gray[200] : gray[700],
  mybg: {
    primaryHover: isLightMode ? gray[50] : grayDarkMode[800],
    primarySolid: isLightMode ? gray[900] : grayDarkMode[900],
    secondary: isLightMode ? gray[50] : grayDarkMode[900],
    secondaryHover: isLightMode ? gray[100] : grayDarkMode[800],
    secondarySubtle: isLightMode ? gray[25] : grayDarkMode[900],
    secondarySolid: isLightMode ? gray[600] : grayDarkMode[600],
    tertiary: isLightMode ? gray[100] : grayDarkMode[800],
    quarterary: isLightMode ? gray[200] : grayDarkMode[700],
    active: isLightMode ? gray[50] : grayDarkMode[800],
    disabled: isLightMode ? gray[100] : grayDarkMode[800],
  },
  myfg: {
    primary: isLightMode ? gray[900] : "#FFFFFF",
    secondary: isLightMode ? gray[700] : grayDarkMode[300],
    secondaryHover: isLightMode ? gray[800] : grayDarkMode[200],
    tertiary: isLightMode ? gray[600] : grayDarkMode[400],
    quarterary: isLightMode ? gray[500] : grayDarkMode[400],
    successPrimary: isLightMode ? success[600] : success[500],
    successSecondary: isLightMode ? success[500] : success[400],
    errorPrimary: isLightMode ? error[600] : error[500],
    errorSecondary: isLightMode ? error[500] : error[400],
    warningPrimary: isLightMode ? warning[600] : warning[500],
    warningSecondary: isLightMode ? warning[500] : warning[400],
  },
  mytext: {
    primaryOnBrand: isLightMode ? base.white : grayDarkMode[50],
    tertiary: isLightMode ? gray[600] : grayDarkMode[300],
    quarterary: isLightMode ? gray[500] : grayDarkMode[400],
    placeholder: isLightMode ? gray[500] : grayDarkMode[500],
    placeholderSubtle: isLightMode ? gray[300] : grayDarkMode[700],
  },
  myborder: {
    primary: isLightMode ? gray[300] : grayDarkMode[700],
    secondary: isLightMode ? gray[200] : grayDarkMode[800],
    tertiary: isLightMode ? gray[100] : grayDarkMode[800],
    disabled: isLightMode ? gray[300] : grayDarkMode[700],
    disabledSubtle: isLightMode ? gray[200] : grayDarkMode[800],
  },
  utility: {
    error: buildUtilityScale(error, isLightMode),
    warning: buildUtilityScale(warning, isLightMode),
    success: buildUtilityScale(success, isLightMode),
    pink: buildUtilityScale(pink, isLightMode),
    blue: buildUtilityScale(blue, isLightMode),
    gray: buildUtilityScale(gray, isLightMode),
    grayBlue: buildUtilityScale(grayBlue, isLightMode),
    orange: buildUtilityScale(orange, isLightMode),
  },
});

export type SemanticColors = ReturnType<typeof getSemanticColors>;

export const useSemanticColors = (): SemanticColors => {
  const colorScheme = useComputedColorScheme("light");
  return useMemo(
    () => getSemanticColors(colorScheme === "light"),
    [colorScheme],
  );
};
